import { makeStyles } from "tss-react/mui";
import { Box, Typography, useTheme } from "@mui/material";
import { MiIcon } from "../MiIcon/MiIcon";

/**
 * Renders a placeholder icon with text and optional helper text.
 *
 * @component
 * @param {object} props - The component props.
 * @param {string} props.icon - The path to the icon.
 * @param {string} props.iconColor - The color of the icon.
 * @param {string} props.text - The main text to display.
 * @param {string} [props.helperText=""] - The helper text to display.
 *
 * @returns {JSX.Element} The rendered placeholder icon component.
 */
export function MiPlaceholderIcon({
  image,
  icon,
  iconColor,
  iconBgColor,
  text,
  helperText = "",
  textProps,
  helperTextProps,
}) {
  const { classes } = useStyles({ iconBgColor, isImage: !!image });
  const theme = useTheme();

  return (
    <Box className={classes.placeholderContainer}>
      <Box className={classes.iconContainer}>
        {image ? (
          <img src={image} alt="placeholder" />
        ) : (
          <MiIcon
            path={icon}
            color={iconColor}
            size={1.5}
            style={{
              padding: theme.spacing(1),
            }}
          />
        )}
      </Box>
      <Box className={classes.textContainer}>
        <Typography
          variant="h6"
          color={theme.palette.text.primary}
          fontWeight={theme.fontWeight.medium}
          fontSize={theme.fontSize.large}
          {...textProps}
        >
          {text}
        </Typography>
        {helperText && (
          <Typography
            variant="body1"
            color={theme.palette.text.secondary}
            fontWeight={theme.fontWeight.regular}
            fontSize={theme.fontSize.medium}
            {...helperTextProps}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

const useStyles = makeStyles()((theme, { iconBgColor, isImage }) => ({
  placeholderContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: isImage ? 0 : theme.spacing(4),
  },
  iconContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 60,
    height: 60,
    borderRadius: 100,
    backgroundColor: iconBgColor || theme.palette.grey.accent,
  },
  textContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
}));
